//@ts-nocheck
import { Col, Form, Row, Select, Spin, Typography } from 'antd';
import type { RuleObject } from 'antd/es/form';
import React, { useMemo } from 'react';
import { Link } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import { ScopeButton } from './buttons';
import { FormInput } from './formInputs';
import { FormSelect } from './formSelect';
import { ScopeCheckbox } from './formCheckbox';
import { FormWrapper } from './formWrappers';
import { ThemedLogo } from './ThemedLogo';
import {
  BoldSpanLargeSize,
  BoldSpanMidSize,
  ColoredBoldSpanLargeSize,
} from './formSpans';
import { AuthA11yToggle } from './AuthA11yToggle';
import { CountryFlagImage } from './countryFlag';
import { useAuthA11yMode } from '../hooks/useAuthA11yMode';
import type { AuthProviderId } from './LoginForm';
import { SsoProviderList, authSsoProviders } from './SsoProviderButton';

export interface SignUpCountry {
  id: string;
  name: string;
  iso: string;
  iso3: string;
  numcode?: number;
  phonecode?: number;
  currency?: string;
  scopeSupported: boolean;
  flag?: string;
}

export interface SignUpCountryFlag {
  name: {
    official: string;
    common: string;
    nativeName?: unknown;
  };
  flag: string;
  flags: { alt: string; png: string; svg: string };
  idd: { root: string; suffixes: string[] };
  cca2: string;
}

export interface SignUpBusinessType {
  id: string;
  name: string;
}

export interface SignUpBusinessSize {
  id: string;
  name: string;
}

/** Subset of user fields used by the sign-up form / register payload. */
export interface SignUpUserDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  code?: string;
  country: string;
  companyName: string;
  personType?: string;
  companySize?: number;
  dduse?: string[];
  subIdentifier?: string;
  tokenCode?: string;
  realmId?: string;
  password?: string;
}

export interface SignUpFormLabels {
  tryTitle: string;
  siteTitle: string;
  forWord: string;
  freeTag: string;
  or: string;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  code: string;
  phone: string;
  country: string;
  companyName: string;
  businessType: string;
  businessSize: string;
  whereToUse: string;
  otherCountries: string;
  supported: string;
  notYet: string;
  termsTitle: string;
  privacyTitle: string;
  iAgreeTo: string;
  and: string;
  termsWarning: string;
  signUp: string;
  alreadyHaveAccount: string;
  login: string;
  requiredField: string;
  invalidEmail: string;
  xeroLogin: string;
  googleLogin: string;
  microsoftLogin: string;
  qboLogin: string;
  zohoLogin: string;
  logoAriaLabel: string;
  emailNotVerified: string;
  intuitLoginLink: string;
  accessibilityMode?: string;
  accessibilityModeHelp?: string;
}

export interface SignUpFormAssets {
  logo: string;
  logoDark?: string;
  xero: string;
  google: string;
  microsoft: string;
  qbo: string;
  qboHover?: string;
  zoho: string;
}

export interface SignUpFormRoutes {
  login: string;
  termsOfUse: string;
  privacyPolicy: string;
}

export interface SignUpFormProps {
  onRegister: (user: SignUpUserDetails, gCaptchaResponse: string) => void;
  onProviderLogin: (provider: AuthProviderId) => void;
  countries: SignUpCountry[];
  countriesExternalData: SignUpCountryFlag[];
  businessTypes: SignUpBusinessType[];
  businessSizes: SignUpBusinessSize[];
  loading?: boolean;
  form: any;
  ssoSignUp?: boolean;
  labels: SignUpFormLabels;
  assets: SignUpFormAssets;
  routes: SignUpFormRoutes;
  captchaSiteKey: string;
  showZoho?: boolean;
  /** When false, hides the accessibility mode switch (GT login/signup). Default true. */
  showA11yToggle?: boolean;
  onA11yDisabled?: () => void;
  logoWidth?: string;
}

/** Default English labels matching generate.TAX en_US signup copy. */
export const DEFAULT_SIGNUP_FORM_LABELS: SignUpFormLabels = {
  tryTitle: 'Try',
  siteTitle: 'generate.TAX',
  forWord: 'for',
  freeTag: 'free',
  or: 'OR',
  description: 'Register with your Email Address',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email Address',
  code: 'Code',
  phone: 'Phone number',
  country: 'Country',
  companyName: 'Company Name',
  businessType: 'Business Type',
  businessSize: 'Business Size',
  whereToUse:
    'In order for us to help you, where do you plan to use generate.TAX?',
  otherCountries: 'Other countries ...',
  supported: 'Supported',
  notYet: 'Not yet',
  termsTitle: 'Terms of Use',
  privacyTitle: 'Privacy Policy',
  iAgreeTo: 'I agree to ',
  and: 'and',
  termsWarning: 'Please indicate that you accept the Terms and Conditions',
  signUp: 'Sign Up',
  alreadyHaveAccount: 'Already have an account?',
  login: 'Login',
  requiredField: 'This field is required',
  invalidEmail: 'The input is not valid Email!',
  xeroLogin: 'Continue with Xero',
  googleLogin: 'Continue with Google',
  microsoftLogin: 'Continue with Microsoft',
  qboLogin: 'Continue with Intuit',
  zohoLogin: 'Continue with Zoho',
  logoAriaLabel: 'generate.TAX logo',
  emailNotVerified: 'Your email is not verified. Please',
  intuitLoginLink: 'log in to your Intuit account',
  accessibilityMode: 'Accessibility mode',
  accessibilityModeHelp:
    'Shows field labels, higher contrast, and clearer focus and links',
};

function renderPhoneCodeOption(country: SignUpCountryFlag): React.JSX.Element {
  const countryName = country.name.common.includes(',')
    ? country.name.common.split(',')[0]
    : country.name.common;
  const phoneCode =
    country.idd.root == '' && country.idd.suffixes.length == 0
      ? '+672'
      : country.idd.root + country.idd.suffixes[0];
  const cca2 = country.cca2 ?? '';

  return (
    <Select.Option
      key={country.name?.official}
      value={country.idd.root + country.idd.suffixes[0]}
      label={`${countryName} ${phoneCode}`}
      style={{ padding: '5px 10px', fontSize: '13px' }}
    >
      <div style={{ display: 'flex' }}>
        <CountryFlagImage
          src={country.flags.png}
          alt={country.flags.alt}
          cca2={cca2}
        />
        <span>{`${countryName} ${phoneCode}`}</span>
      </div>
    </Select.Option>
  );
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onRegister,
  onProviderLogin,
  countries,
  countriesExternalData,
  businessTypes,
  businessSizes,
  loading,
  form,
  ssoSignUp = false,
  labels,
  assets,
  routes,
  captchaSiteKey,
  showZoho = true,
  showA11yToggle = true,
  onA11yDisabled,
  logoWidth,
}) => {
  const { a11yMode, handleA11yModeChange, authFormClassName } =
    useAuthA11yMode(onA11yDisabled);
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(false);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const captchaEnabled = Boolean(captchaSiteKey);

  React.useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Redux Toolkit freezes state — never mutate `countries`; derive flag URLs.
  const countriesWithFlags = useMemo(
    () =>
      countries.map((country) => ({
        ...country,
        flag:
          countriesExternalData.find(
            (flag) => flag.cca2.toLowerCase() === country.iso.toLowerCase(),
          )?.flags.png ?? country.flag,
      })),
    [countries, countriesExternalData],
  );

  const validation = (
    _rule: RuleObject,
    value: boolean,
    callback: (error?: string) => void,
  ) => {
    if (value) {
      return callback();
    }
    return callback(labels.termsWarning);
  };

  const handleSubmit = async (
    values: SignUpUserDetails & { Terms?: boolean; emailVerified?: string },
  ) => {
    const gCaptchaResponse: string = captchaEnabled
      ? (await recaptchaRef.current?.executeAsync()) || ''
      : '';
    const {
      Terms: _terms,
      emailVerified: _emailVerified,
      ...userDetails
    } = values;
    onRegister(
      {
        ...userDetails,
        password: userDetails.password ?? '',
      },
      gCaptchaResponse,
    );
  };

  const renderCountryWithFlag = (country: SignUpCountry): React.ReactNode => {
    return (
      <div style={{ display: 'flex' }}>
        <CountryFlagImage
          src={country.flag}
          alt={country.name}
          cca2={country.iso ?? ''}
        />
        {country.name}
      </div>
    );
  };

  const renderOtherCountries = () => {
    const supportedCountries = countriesWithFlags.filter(
      (country) => country.scopeSupported,
    );
    const notSupportedCountries = countriesWithFlags.filter(
      (country) => !country.scopeSupported,
    );
    return (
      <>
        <Select.OptGroup key="supported" label={labels.supported}>
          {supportedCountries &&
            supportedCountries.map((c) => (
              <Select.Option
                key={c.iso}
                value={
                  c.name.includes(' ')
                    ? c.iso3.toLowerCase()
                    : c.name.toLowerCase()
                }
              >
                {renderCountryWithFlag(c)}
              </Select.Option>
            ))}
        </Select.OptGroup>
        <Select.OptGroup key="not-yet" label={labels.notYet}>
          {notSupportedCountries &&
            notSupportedCountries.map((c) => (
              <Select.Option
                key={c.iso}
                value={
                  c.name.includes(' ')
                    ? c.iso3.toLowerCase()
                    : c.name.toLowerCase()
                }
              >
                {renderCountryWithFlag(c)}
              </Select.Option>
            ))}
        </Select.OptGroup>
      </>
    );
  };

  return (
    <FormWrapper>
      <Spin spinning={loading} size="large">
        <Row justify="center" style={{ paddingTop: '20px' }}>
          <Col span={12}>
            <Row justify="center">
              <Link to={routes.login} aria-label={labels.logoAriaLabel}>
                <ThemedLogo
                  src={assets.logo}
                  srcDark={assets.logoDark}
                  alt=""
                  width={logoWidth}
                />
              </Link>
            </Row>
            {showA11yToggle ? (
              <AuthA11yToggle
                placement="header"
                checked={a11yMode}
                onChange={handleA11yModeChange}
                label={labels.accessibilityMode}
                helpText={labels.accessibilityModeHelp}
              />
            ) : null}
            <Row justify="center">
              <Typography.Title
                level={1}
                style={{
                  fontSize: '24px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  margin: '12px 0',
                  textAlign: 'center',
                }}
              >
                {labels.tryTitle}
                <BoldSpanLargeSize> {labels.siteTitle} </BoldSpanLargeSize>
                {labels.forWord}
                <ColoredBoldSpanLargeSize>
                  {' '}
                  {labels.freeTag}
                </ColoredBoldSpanLargeSize>
              </Typography.Title>
            </Row>
            <SsoProviderList
              layout="wrap"
              showZoho={showZoho}
              items={authSsoProviders({
                labels,
                assets,
                onProviderLogin,
              })}
            />
            <Row justify={'center'} align={'middle'} style={{ height: '50px' }}>
              <Col
                span={4}
                style={{
                  backgroundColor: 'var(--color-border, #d5dce0)',
                  height: '1px',
                }}
              />
              <Col
                span={2}
                style={{
                  textAlign: 'center',
                  color: 'var(--color-text-dark, #042a2a)',
                  fontSize: '18px',
                  lineHeight: '24px',
                  fontWeight: 600,
                }}
              >
                {labels.or}
              </Col>
              <Col
                span={4}
                style={{
                  backgroundColor: 'var(--color-border, #d5dce0)',
                  height: '1px',
                }}
              />
            </Row>
            <Row justify="center">
              <Typography.Text
                style={{
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  textAlign: 'center',
                  marginBottom: '30px',
                  marginTop: '20px',
                  color: 'var(--color-text-dark, #042a2a)',
                }}
              >
                {labels.description}
              </Typography.Text>
            </Row>
            <Form
              layout="vertical"
              requiredMark={false}
              className={authFormClassName}
              onFinish={handleSubmit}
              style={{ marginBottom: '30px' }}
              form={form}
            >
              {captchaEnabled ? (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={captchaSiteKey}
                />
              ) : null}
              <Row
                className="scope-signup-fields"
                justify="center"
                align={'middle'}
                gutter={24}
              >
                <Col span={12} style={{ paddingLeft: '50px' }}>
                  <Form.Item
                    name="firstName"
                    label={labels.firstName}
                    rules={[{ required: true, message: labels.requiredField }]}
                  >
                    <FormInput width="100%" placeholder={labels.firstName} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="lastName"
                    label={labels.lastName}
                    rules={[{ required: true, message: labels.requiredField }]}
                  >
                    <FormInput placeholder={labels.lastName} />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ paddingLeft: '50px' }}>
                  <Form.Item
                    name="email"
                    label={labels.email}
                    rules={[
                      { required: true, message: labels.requiredField },
                      {
                        type: 'email',
                        message: labels.invalidEmail,
                      },
                    ]}
                  >
                    <FormInput
                      width="100%"
                      placeholder={labels.email}
                      disabled={ssoSignUp}
                    />
                  </Form.Item>
                </Col>
                <Col
                  span={12}
                  className="scope-signup-fields__phone"
                  style={{ display: 'flex' }}
                >
                  <Form.Item
                    name="code"
                    label={labels.code}
                    rules={[{ required: true, message: labels.requiredField }]}
                    style={{ width: '29%', marginRight: '2%' }}
                  >
                    <FormSelect
                      width={'100%'}
                      height="45px"
                      placeholder={labels.code}
                      aria-label={labels.code}
                      popupClassName="scope-signup-phone-code-dropdown"
                      dropdownStyle={
                        isNarrowViewport
                          ? {
                              maxWidth: 'calc(100vw - 24px)',
                              minWidth: 0,
                              width: 'calc(100vw - 24px)',
                            }
                          : { minWidth: 420 }
                      }
                      popupMatchSelectWidth={isNarrowViewport}
                      placement="bottomLeft"
                      optionLabelProp="value"
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                      showSearch={true}
                      filterOption={(input, option) => {
                        const inputLower = input.toLowerCase();
                        const valueLower = option?.value?.toLowerCase() || '';
                        const labelLower = option?.label?.toLowerCase() || '';
                        return (
                          valueLower.includes(inputLower) ||
                          labelLower.includes(inputLower)
                        );
                      }}
                    >
                      {countriesExternalData &&
                        countriesExternalData.map((c) =>
                          renderPhoneCodeOption(c),
                        )}
                    </FormSelect>
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={labels.phone}
                    rules={[{ required: true, message: labels.requiredField }]}
                    style={{ width: '59%' }}
                  >
                    <FormInput width={'100%'} placeholder={labels.phone} />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ paddingLeft: '50px' }}>
                  <Form.Item
                    name="country"
                    label={labels.country}
                    rules={[{ required: true, message: labels.requiredField }]}
                  >
                    <FormSelect
                      width="100%"
                      height="45px"
                      placeholder={labels.country}
                      aria-label={labels.country}
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                      showSearch={true}
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {countriesWithFlags &&
                        countriesWithFlags.map((c) => (
                          <Select.Option
                            key={c.iso}
                            value={c.iso}
                            label={c.name}
                          >
                            {renderCountryWithFlag(c)}
                          </Select.Option>
                        ))}
                    </FormSelect>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="companyName"
                    label={labels.companyName}
                    rules={[{ required: true, message: labels.requiredField }]}
                  >
                    <FormInput placeholder={labels.companyName} />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ paddingLeft: '50px' }}>
                  <Form.Item name="personType" label={labels.businessType}>
                    <FormSelect
                      width="100%"
                      height="45px"
                      placeholder={labels.businessType}
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                    >
                      {businessTypes &&
                        businessTypes.map((bt) => (
                          <Select.Option key={bt.id} value={bt.id}>
                            {bt.name}
                          </Select.Option>
                        ))}
                    </FormSelect>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="companySize" label={labels.businessSize}>
                    <FormSelect
                      height="45px"
                      placeholder={labels.businessSize}
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                    >
                      {businessSizes &&
                        businessSizes.map((bs) => (
                          <Select.Option
                            key={bs.id}
                            value={parseInt(bs.id.replace('_', ''))}
                          >
                            {bs.name}
                          </Select.Option>
                        ))}
                    </FormSelect>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="center" align={'middle'}>
                <Col
                  span={24}
                  style={{
                    marginTop: '10px',
                    marginBottom: '10px',
                    textAlign: 'center',
                  }}
                >
                  <Typography.Text
                    style={{
                      color: 'var(--color-text-dark, #042a2a)',
                      fontSize: '14px',
                      lineHeight: '24px',
                      fontWeight: 400,
                    }}
                  >
                    {labels.whereToUse}
                  </Typography.Text>
                </Col>
              </Row>
              <Row className="scope-signup-fields" gutter={24}>
                <Col span={24} className="scope-signup-other-countries">
                  <Form.Item name="dduse" label={labels.otherCountries}>
                    <FormSelect
                      width="100%"
                      height="45px"
                      mode="multiple"
                      placeholder={labels.otherCountries}
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                    >
                      {renderOtherCountries()}
                    </FormSelect>
                  </Form.Item>
                </Col>
              </Row>
              <Row className="scope-signup-actions" justify="center">
                <Col span={24}>
                  <Form.Item
                    name="Terms"
                    valuePropName="checked"
                    className="scope-signup-terms"
                    label={
                      a11yMode
                        ? `${labels.termsTitle} & ${labels.privacyTitle}`
                        : undefined
                    }
                    rules={[{ validator: validation }]}
                  >
                    <ScopeCheckbox>
                      <span className="link-text" style={{ fontSize: '13px' }}>
                        {labels.iAgreeTo}{' '}
                        <a
                          className="link"
                          href={routes.termsOfUse}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {labels.termsTitle}
                        </a>{' '}
                        {labels.and}{' '}
                        <a
                          className="link"
                          href={routes.privacyPolicy}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {labels.privacyTitle}
                        </a>
                      </span>
                    </ScopeCheckbox>
                  </Form.Item>
                  {form.getFieldValue('emailVerified') === 'false' && (
                    <Typography.Text type="danger" style={{ fontSize: '13px' }}>
                      {labels.emailNotVerified}{' '}
                      <a
                        href="https://accounts.intuit.com/app/account-manager/security"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {labels.intuitLoginLink}
                      </a>
                      .
                    </Typography.Text>
                  )}
                  <Form.Item className="scope-signup-cta">
                    <ScopeButton
                      type="primary"
                      htmlType="submit"
                      width="100%"
                      disabled={form.getFieldValue('emailVerified') === 'false'}
                    >
                      {labels.signUp}
                    </ScopeButton>
                  </Form.Item>
                  <Typography.Text
                    className="scope-signup-login-prompt"
                    style={{ fontSize: '13px' }}
                  >
                    <BoldSpanMidSize>
                      {labels.alreadyHaveAccount}{' '}
                    </BoldSpanMidSize>
                    <Link className="sign-up-link" to={routes.login}>
                      {labels.login}
                    </Link>
                  </Typography.Text>
                  <Form.Item name="subIdentifier" hidden />
                  <Form.Item name="tokenCode" hidden />
                  <Form.Item name="realmId" hidden />
                  <Form.Item name="emailVerified" hidden />
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Spin>
    </FormWrapper>
  );
};
